import { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { getErrorMessage } from '../services/errorMessages';
import { FiPlus, FiTrash2, FiEdit2, FiX, FiEye, FiClock, FiFileText, FiUsers, FiPrinter } from 'react-icons/fi';
import Calendar from '../components/Calendar';
import Dropdown from '../components/Dropdown';
import ConfirmModal from '../components/ConfirmModal';
import './Pages.css';
import './Attachments.css';

const Clients = () => {
  const { isAdmin } = useAuth();
  const [clients, setClients] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [confirmAction, setConfirmAction] = useState(null);
  const [showDetail, setShowDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showView, setShowView] = useState(false);
  const [viewData, setViewData] = useState(null);

  const openDetail = async (client) => {
    setDetailLoading(true);
    setShowDetail({ client });
    try {
      const { data } = await api.get(`/clients/${client.id}/detail`);
      setShowDetail(data);
    } catch { /* ignored */ }
    finally { setDetailLoading(false); }
  };

  const openView = async (fa) => {
    try {
      const { data } = await api.get(`/filled-attachments/${fa.id}`);
      setViewData(data);
      setShowView(true);
    } catch { /* ignored */ }
  };

  const deleteAttachment = (fa) => {
    setConfirmAction({
      title: 'დანართის სამუდამო წაშლა',
      message: `ნამდვილად გსურთ "${fa.template_title || 'დანართი'}" სამუდამოდ წაშლა? ეს მოქმედება შეუქცევადია!`,
      type: 'delete',
      confirmText: 'სამუდამოდ წაშლა',
      onConfirm: async () => {
        try {
          await api.delete(`/filled-attachments/${fa.id}`);
          // Refresh detail
          if (showDetail?.client) openDetail(showDetail.client);
        } catch { /* ignored */ }
        setConfirmAction(null);
      },
    });
  };

  const formatTimeSec = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  };

  const fmtDuration = (sec) => {
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    return `${h}სთ ${m}წთ`;
  };

  const emptyForm = { first_name: '', last_name: '', address: '', birth_date: '', gender: '', personal_number: '', mobile: '' };
  const [form, setForm] = useState(emptyForm);

  const fetchClients = async () => {
    try {
      const { data } = await api.get('/clients');
      setClients(data);
    } catch {
      // fetch failed silently
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchClients(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (editing) {
        await api.put(`/clients/${editing}`, form);
      } else {
        await api.post('/clients', form);
      }
      setShowModal(false);
      setEditing(null);
      setForm(emptyForm);
      fetchClients();
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleEdit = (client) => {
    setForm({
      ...client,
      birth_date: client.birth_date ? client.birth_date.split('T')[0] : '',
    });
    setEditing(client.id);
    setShowModal(true);
  };

  const handleDelete = (id) => {
    setConfirmAction({
      title: 'ბენეფიციარის წაშლა',
      message: 'ნამდვილად გსურთ წაშლა? ეს მოქმედება შეუქცევადია.',
      type: 'delete',
      onConfirm: async () => {
        try { await api.delete(`/clients/${id}`); fetchClients(); } catch { /* ignored */ }
        setConfirmAction(null);
      },
    });
  };

  const filtered = clients.filter((c) =>
    `${c.first_name} ${c.last_name} ${c.personal_number}`.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>ბენეფიციარები</h1>
          <p className="page-subtitle">ბენეფიციარების მართვა</p>
        </div>
        <div className="header-actions">
          <input
            className="search-input"
            placeholder="ძიება..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {isAdmin && (
            <button className="btn-primary" onClick={() => { setForm(emptyForm); setEditing(null); setShowModal(true); }}>
              <FiPlus /> დამატება
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="loading">იტვირთება...</div>
      ) : (
        <>
          {/* Card view for mobile */}
          <div className="cards-grid">
            {filtered.map((c) => (
              <div key={c.id} className="data-card">
                <div className="card-header-row">
                  <div className="card-avatar">{c.first_name[0]}{c.last_name[0]}</div>
                  <div>
                    <h3>{c.first_name} {c.last_name}</h3>
                    <span className="card-pn">{c.personal_number}</span>
                  </div>
                </div>
                <div className="card-details">
                  <div className="card-detail"><span>მისამართი:</span> {c.address || '-'}</div>
                  <div className="card-detail"><span>დაბადების თარიღი:</span> {c.birth_date ? new Date(c.birth_date).toLocaleDateString('ka-GE') : '-'}</div>
                  <div className="card-detail"><span>სქესი:</span> {c.gender === 'male' ? 'მამრობითი' : c.gender === 'female' ? 'მდედრობითი' : '-'}</div>
                  <div className="card-detail"><span>მობილური:</span> {c.mobile || '-'}</div>
                </div>
                {isAdmin && (
                  <div className="card-actions">
                    <button className="btn-icon btn-edit" onClick={() => openDetail(c)} title="დეტალები"><FiEye /></button>
                    <button className="btn-icon btn-edit" onClick={() => handleEdit(c)}><FiEdit2 /></button>
                    <button className="btn-icon btn-danger" onClick={() => handleDelete(c.id)}><FiTrash2 /></button>
                  </div>
                )}
              </div>
            ))}
            {filtered.length === 0 && <div className="empty-state-card">ბენეფიციარები არ არის</div>}
          </div>

          {/* Table view for desktop */}
          <div className="table-container desktop-only">
            <table className="data-table">
              <thead>
                <tr>
                  <th>სახელი</th>
                  <th>გვარი</th>
                  <th>პ/ნ</th>
                  <th>მისამართი</th>
                  <th>დაბ. თარიღი</th>
                  <th>სქესი</th>
                  <th>მობილური</th>
                  {isAdmin && <th>მოქმედება</th>}
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c.id}>
                    <td>{c.first_name}</td>
                    <td>{c.last_name}</td>
                    <td>{c.personal_number}</td>
                    <td>{c.address || '-'}</td>
                    <td>{c.birth_date ? new Date(c.birth_date).toLocaleDateString('ka-GE') : '-'}</td>
                    <td>{c.gender === 'male' ? 'მამრობითი' : c.gender === 'female' ? 'მდედრობითი' : '-'}</td>
                    <td>{c.mobile || '-'}</td>
                    {isAdmin && (
                      <td>
                        <div className="action-btns">
                          <button className="btn-icon btn-edit" onClick={() => openDetail(c)} title="დეტალები"><FiEye /></button>
                          <button className="btn-icon btn-edit" onClick={() => handleEdit(c)} title="რედაქტირება"><FiEdit2 /></button>
                          <button className="btn-icon btn-danger" onClick={() => handleDelete(c.id)} title="წაშლა"><FiTrash2 /></button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={isAdmin ? "8" : "7"} className="empty-state">ბენეფიციარები არ არის</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => { setShowModal(false); setEditing(null); }}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editing ? 'რედაქტირება' : 'ახალი ბენეფიციარი'}</h3>
              <button className="btn-icon" onClick={() => { setShowModal(false); setEditing(null); }}><FiX /></button>
            </div>
            <form onSubmit={handleSubmit} className="modal-form">
              {error && <div className="error-message">{error}</div>}
              <div className="form-row">
                <div className="form-group">
                  <label>სახელი</label>
                  <input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>გვარი</label>
                  <input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} required />
                </div>
              </div>
              <div className="form-group">
                <label>პირადი ნომერი</label>
                <input inputMode="numeric" pattern="[0-9]*" value={form.personal_number} onChange={(e) => setForm({ ...form, personal_number: e.target.value.replace(/\D/g, '') })} required />
              </div>
              <div className="form-group">
                <label>მისამართი</label>
                <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
              </div>
              <div className="form-row">
                <Calendar
                  label="დაბადების თარიღი"
                  value={form.birth_date}
                  onChange={(val) => setForm({ ...form, birth_date: val })}
                  placeholder="აირჩიეთ თარიღი"
                />
                <Dropdown
                  label="სქესი"
                  value={form.gender}
                  onChange={(val) => setForm({ ...form, gender: val })}
                  options={[
                    { value: 'male', label: 'მამრობითი' },
                    { value: 'female', label: 'მდედრობითი' },
                  ]}
                  placeholder="აირჩიეთ სქესი"
                />
              </div>
              <div className="form-group">
                <label>მობილური</label>
                <input inputMode="numeric" pattern="[0-9]*" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value.replace(/\D/g, '') })} />
              </div>
              <button type="submit" className="btn-primary btn-full" disabled={!form.first_name.trim() || !form.last_name.trim() || !form.personal_number}>{editing ? 'შენახვა' : 'დამატება'}</button>
            </form>
          </div>
        </div>
      )}

      {/* Client detail modal */}
      {showDetail && (
        <div className="modal-overlay" onClick={() => setShowDetail(null)}>
          <div className="modal attachment-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{showDetail.client?.first_name} {showDetail.client?.last_name}</h3>
              <button className="btn-icon" onClick={() => setShowDetail(null)}><FiX /></button>
            </div>
            {detailLoading ? <div className="loading" style={{padding:40}}>იტვირთება...</div> : showDetail.stats && (
              <div className="client-detail-view">
                {/* Client info */}
                <div className="cdv-info">
                  <div className="cdv-avatar">{showDetail.client.first_name[0]}{showDetail.client.last_name[0]}</div>
                  <div className="cdv-main">
                    <h2>{showDetail.client.first_name} {showDetail.client.last_name}</h2>
                    <span className="cdv-pn">{showDetail.client.personal_number}</span>
                  </div>
                </div>
                <div className="cdv-fields">
                  {showDetail.client.mobile && <div className="cdv-field"><span>მობილური</span><strong>{showDetail.client.mobile}</strong></div>}
                  {showDetail.client.address && <div className="cdv-field"><span>მისამართი</span><strong>{showDetail.client.address}</strong></div>}
                  {showDetail.client.birth_date && <div className="cdv-field"><span>დაბადების თარიღი</span><strong>{new Date(showDetail.client.birth_date).toLocaleDateString('ka-GE')}</strong></div>}
                  {showDetail.client.gender && <div className="cdv-field"><span>სქესი</span><strong>{showDetail.client.gender === 'male' ? 'მამრობითი' : 'მდედრობითი'}</strong></div>}
                </div>

                {/* Stats */}
                <div className="cdv-stats">
                  <div className="cdv-stat"><FiFileText /><div><strong>{showDetail.stats.total_attachments}</strong><span>სულ დანართი</span></div></div>
                  <div className="cdv-stat"><FiClock /><div><strong>{fmtDuration(showDetail.stats.total_duration)}</strong><span>ჯამური დრო</span></div></div>
                  <div className="cdv-stat"><FiUsers /><div><strong>{(showDetail.users || []).length}</strong><span>თანამშრომელი</span></div></div>
                </div>

                {/* Assigned users */}
                {showDetail.users && showDetail.users.length > 0 && (
                  <div className="cdv-section">
                    <h4>პერსონალური ასისტენტის სახელი და გვარი</h4>
                    <div className="cdv-users">
                      {showDetail.users.map((u) => (
                        <div key={u.id} className="cdv-user-chip">
                          <span className="cdv-chip-av">{u.first_name[0]}{u.last_name[0]}</span>
                          {u.first_name} {u.last_name}
                          <span className="cdv-chip-pn">{u.personal_number}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Attachment history */}
                <div className="cdv-section">
                  <h4>შევსებული დანართები ({showDetail.attachments?.length || 0})</h4>
                  <div className="cdv-period-stats">
                    <span>დღეს: <strong>{showDetail.stats.today}</strong></span>
                    <span>კვირაში: <strong>{showDetail.stats.week}</strong></span>
                    <span>თვეში: <strong>{showDetail.stats.month}</strong></span>
                  </div>
                  <div className="cdv-att-list">
                    {(showDetail.attachments || []).map((fa) => (
                      <div key={fa.id} className={`cdv-att-row ${fa.hidden_by_user ? 'cdv-att-hidden' : ''}`} onClick={() => openView(fa)} style={{ cursor: 'pointer' }}>
                        <FiEye style={{ color: '#667eea', flexShrink: 0 }} />
                        <div className="cdv-att-info">
                          <span className="cdv-att-title">
                            {fa.template_title}
                            {fa.hidden_by_user && <span className="att-hidden-badge">წაშლილი</span>}
                          </span>
                          <span className="cdv-att-meta">
                            {fa.user_first_name} {fa.user_last_name} | {new Date(fa.completed_at).toLocaleString('ka-GE', { hour12: false })}
                          </span>
                        </div>
                        {fa.duration_seconds > 0 && <span className="cdv-att-dur"><FiClock /> {fmtDuration(fa.duration_seconds)}</span>}
                        <button className="btn-icon btn-danger" onClick={(e) => { e.stopPropagation(); deleteAttachment(fa); }} title="სამუდამო წაშლა"><FiTrash2 /></button>
                      </div>
                    ))}
                    {(!showDetail.attachments || showDetail.attachments.length === 0) && <div className="empty-hint" style={{padding:16, textAlign:'center'}}>დანართები არ არის</div>}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* View filled attachment modal */}
      {showView && viewData && (
        <div className="modal-overlay" onClick={() => setShowView(false)}>
          <div className="modal attachment-modal print-area" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header no-print">
              <h3>{viewData.template_title}</h3>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn-icon" onClick={() => window.print()} title="ბეჭდვა"><FiPrinter /></button>
                <button className="btn-icon btn-danger" onClick={() => { setShowView(false); deleteAttachment(viewData); }} title="წაშლა"><FiTrash2 /></button>
                <button className="btn-icon" onClick={() => setShowView(false)}><FiX /></button>
              </div>
            </div>
            <div className="paper">
              <div className="paper-title-right">{viewData.template_title}</div>
              {viewData.template_main_title && <div className="paper-center"><div className="paper-main-title">{viewData.template_main_title}</div></div>}
              {viewData.template_subtitle && <div className="paper-center"><div className="paper-subtitle">{viewData.template_subtitle}</div></div>}
              <div className="paper-fields">
                {(viewData.template_header_fields || []).map((f, i) => (
                  <div key={i} className="paper-field">
                    <span className="paper-field-label">{f.label}</span>
                    <span className="paper-field-value">
                      {f.type === 'client_dropdown' ? `${viewData.client_first_name || ''} ${viewData.client_last_name || ''} - ${viewData.client_pn || ''}` :
                       f.type === 'auto_user' ? `${viewData.user_first_name || ''} ${viewData.user_last_name || ''} - ${viewData.user_pn || ''}` :
                       (viewData.header_values || {})[i] || ''}
                    </span>
                  </div>
                ))}
              </div>
              {(() => {
                const cols = (viewData.template_columns || []).map((c, i) => ({ ...c, idx: i }));
                const rd = viewData.row_data || {};
                const rows = rd.rows || (Array.isArray(rd) ? rd : []);
                const fullRows = rd.fullRows || {};
                const rowCount = Math.max(rows.length, 1);

                return (
                  <div className="paper-table-wrap">
                    <table className="paper-table">
                      <thead><tr><th className="paper-col-num">№</th>
                        {cols.map((c) => (<th key={c.idx} className={c.fullRow ? 'paper-col-fullrow' : ''} style={c.width ? {width: `${c.width}%`} : undefined}>{c.name}</th>))}
                      </tr></thead>
                      <tbody>
                        {rows.map((row, ri) => (
                          <tr key={ri}>
                            <td className="paper-col-num">{ri + 1}</td>
                            {cols.map((c) => {
                              if (c.fullRow && ri === 0) {
                                const items = fullRows[c.idx] || [];
                                return (
                                  <td key={c.idx} rowSpan={rowCount} className="paper-cell-fullrow" style={{ verticalAlign: 'middle', fontSize: 13 }}>
                                    {items.map((val, vi) => (
                                      <div key={vi} style={{ padding: '2px 0' }}>
                                        {c.type === 'signature' && val ? <img src={val} alt="" style={{ height: 25, objectFit: 'contain' }} /> : (val || '')}
                                      </div>
                                    ))}
                                  </td>
                                );
                              }
                              if (c.fullRow) return null;
                              return (
                                <td key={c.idx}>
                                  {c.type === 'signature' && row[c.idx] ? <img src={row[c.idx]} alt="" style={{ height: 25, objectFit: 'contain' }} /> : (row[c.idx] || '')}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
              {viewData.note && <div className="paper-note"><strong>შენიშვნა:</strong> {viewData.note}</div>}
              {false && viewData.duration_seconds && (
                <div className="paper-duration">ხანგრძლივობა: {formatTimeSec(viewData.duration_seconds)}</div>
              )}
            </div>
          </div>
        </div>
      )}

      {confirmAction && <ConfirmModal {...confirmAction} onCancel={() => setConfirmAction(null)} />}
    </div>
  );
};

export default Clients;
